/**
 * Helpers de fecha — fuente única de verdad.
 * Semana = Lunes -> Domingo (decisión del rediseño; el front viejo usaba Sáb -> Vie).
 * Se evita la dependencia de moment para casos triviales.
 *
 * IMPORTANTE: los strings "YYYY-MM-DD" se parsean como hora LOCAL (mediodía), no UTC.
 * `new Date("2026-05-21")` parsea como UTC y en TZ negativas (Argentina UTC-3) cae un día
 * antes. `toDate()` evita ese corrimiento.
 */

/** Normaliza Date | "YYYY-MM-DD" | ISO a un Date local a mediodía (evita TZ y DST). */
export function toDate(fecha) {
  if (fecha instanceof Date) {
    const d = new Date(fecha)
    d.setHours(12, 0, 0, 0)
    return d
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(fecha))
  if (m) {
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0, 0)
  }
  const d = new Date(fecha)
  d.setHours(12, 0, 0, 0)
  return d
}

/** Date | string -> "YYYY-MM-DD" usando partes locales (evita corrimiento por TZ). */
export function formatFecha(fecha) {
  const d = toDate(fecha)
  const year = d.toLocaleString('default', { year: 'numeric' })
  const month = d.toLocaleString('default', { month: '2-digit' })
  const day = d.toLocaleString('default', { day: '2-digit' })
  return `${year}-${month}-${day}`
}

/** Suma (o resta) días a una fecha y devuelve un Date nuevo (local, mediodía). */
export function addDays(fecha, n) {
  const d = toDate(fecha)
  d.setDate(d.getDate() + n)
  return d
}

/** Lunes de la semana que contiene a `fecha` (Date local, mediodía). */
export function startOfWeek(fecha) {
  const d = toDate(fecha)
  const day = d.getDay() // 0=Dom .. 6=Sáb
  const diff = day === 0 ? -6 : 1 - day // mover a Lunes
  d.setDate(d.getDate() + diff)
  return d
}

/** Rango de la semana (Lun -> Dom) que contiene a `fecha`. Devuelve strings "YYYY-MM-DD". */
export function weekRange(fecha) {
  const start = startOfWeek(fecha)
  const end = addDays(start, 6)
  return { start: formatFecha(start), end: formatFecha(end) }
}

/** Array de 7 strings "YYYY-MM-DD", Lunes -> Domingo, para la semana de `fecha`. */
export function weekDays(fecha) {
  const start = startOfWeek(fecha)
  return Array.from({ length: 7 }, (_, i) => formatFecha(addDays(start, i)))
}

/**
 * Columnas de la grilla semanal: Sab y Dom del finde anterior + Lun -> Vie.
 * Ej. si la semana calendaria es Lun 8 – Dom 14: Sab 6, Dom 7, Lun 8 … Vie 12.
 */
export function weekDaysGrid(fecha) {
  const monday = startOfWeek(fecha)
  const sat = formatFecha(addDays(monday, -2))
  const sun = formatFecha(addDays(monday, -1))
  const weekdays = Array.from({ length: 5 }, (_, i) => formatFecha(addDays(monday, i)))
  return [sat, sun, ...weekdays]
}

/** Rango de datos de la grilla semanal (Sáb previo -> Vie). */
export function weekRangeGrid(fecha) {
  const days = weekDaysGrid(fecha)
  return { start: days[0], end: days[days.length - 1] }
}

/** ¿`fecha` (Date | string) es hoy? */
export function isToday(fecha) {
  return formatFecha(fecha) === formatFecha(new Date())
}

const NOMBRES_DIAS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado']
const NOMBRES_MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
]

/** "Lunes 19 May" para encabezados. */
export function labelDia(fecha) {
  const d = toDate(fecha)
  return `${NOMBRES_DIAS[d.getDay()]} ${d.getDate()} ${NOMBRES_MESES[d.getMonth()].slice(0, 3)}`
}

export { NOMBRES_DIAS, NOMBRES_MESES }
